import type {
  GetActivitiesDTO,
  CreateActivityDTO,
  GetActivitiesPagedDTO,
} from './dto/activity.dto'
import { API } from '.'
import { API_BASE_URL } from '@/config'
import { createApiError } from '@/helper/apiError.helpers'
import Cookies from 'js-cookie'
import dayjs from 'dayjs'

export type ActivityAttachmentDTO = {
  id: number
  entityType: 'ACTIVITY'
  entityId: number
  filename: string
  contentType: string
  fileSize: number
  uploadedBy: string
  uploadedAt: string
}

export type ActivityAttachmentListResponse = {
  attachments: ActivityAttachmentDTO[]
  totalSize: number
  count: number
}

/**
 * Serialize a Date as naive local wall-clock time (no offset).
 * The backend binds from/to to LocalDateTime and stores activity dates the same way,
 * so sending toISOString() (UTC) would shift the range by the local offset.
 */
export function toLocalDateTimeParam(date: Date) {
  return dayjs(date).format('YYYY-MM-DD[T]HH:mm:ss')
}

export async function getActivitiesInRange(from: Date, to: Date) {
  const pageSize = 100 // choose a safe value; backend may cap it anyway
  let page = 1

  const all: GetActivitiesPagedDTO['items'] = []
  let totalItems = Infinity

  while (all.length < totalItems) {
    const params = new URLSearchParams({
      from: toLocalDateTimeParam(from),
      to: toLocalDateTimeParam(to),
      page: String(page),
      pageSize: String(pageSize),
    })

    const res = await API<GetActivitiesPagedDTO>(
      `/activities?${params.toString()}`,
      {},
      'Fehler beim Laden der Aktivitäten',
      'ACTIVITIES_FETCH_ERROR'
    )

    all.push(...(res.items ?? []))
    totalItems = typeof res.totalItems === 'number' ? res.totalItems : all.length

    if (!res.items || res.items.length === 0) break // safety (avoid infinite loop)
    page += 1
  }

  // IMPORTANT: keep the old return shape so components don’t change
  const out: GetActivitiesDTO = { items: all }
  return out
}

export async function createActivity(activity: CreateActivityDTO) {
  const payload: any = {
    ...activity,
    date: activity.date instanceof Date ? activity.date.toISOString() : activity.date,
  }

  if (!payload.contact) delete payload.contact
  if (!payload.services || payload.services.length === 0) delete payload.services
  if (!payload.billingVariant) delete payload.billingVariant

  // NEW
  if (!payload.orderId) delete payload.orderId

  // keep boolean always (don’t delete false)
  if (payload.skipTojQuantityCheck == null) payload.skipTojQuantityCheck = false

  // omit times only if empty
  if (!payload.activityStartTime) delete payload.activityStartTime
  if (!payload.activityEndTime) delete payload.activityEndTime

  // advisorNumber: KEEP empty string (per backend response contract)
  if (payload.advisorNumber == null) payload.advisorNumber = ''

  return await API(
    `/activities`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Fehler beim Erstellen der Aktivität',
    'ACTIVITY_CREATE_ERROR'
  )
}

export async function updateActivity(id: string, activity: CreateActivityDTO) {
  const payload: any = {
    ...activity,
    date: activity.date instanceof Date ? activity.date.toISOString() : activity.date,
  }

  if (payload.contact === undefined) delete payload.contact
  if (!payload.services || payload.services.length === 0) delete payload.services
  if (!payload.billingVariant) delete payload.billingVariant

  // NEW
  if (!payload.orderId) delete payload.orderId

  // keep boolean always (don’t delete false)
  if (payload.skipTojQuantityCheck == null) payload.skipTojQuantityCheck = false

  // omit times only if empty
  if (!payload.activityStartTime) delete payload.activityStartTime
  if (!payload.activityEndTime) delete payload.activityEndTime

  // advisorNumber: KEEP empty string (per backend response contract)
  if (payload.advisorNumber == null) payload.advisorNumber = ''

  return await API(
    `/activities/${id}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    'Fehler beim Aktualisieren der Aktivität',
    'ACTIVITY_UPDATE_ERROR'
  )
}

export async function deleteActivity(id: string) {
  return await API(
    `/activities/${id}`,
    {
      method: 'DELETE',
    },
    'Fehler beim Löschen der Akivität',
    'ACTIVITY_DELETE_ERROR'
  )
}

export async function listActivityAttachments(activityId: string | number) {
  return await API<ActivityAttachmentListResponse>(
    `/activities/${activityId}/attachments`,
    {
      method: 'GET',
    },
    'Fehler beim Laden der Anhänge',
    'ACTIVITY_ATTACHMENTS_LIST_ERROR'
  )
}
export async function uploadActivityAttachments(activityId: number, file: File) {
  const form = new FormData()
  form.append('files', file, file.name)

  const csrf = localStorage.getItem('CSRF_TOKEN') || Cookies.get('CSRF_TOKEN') || ''

  const res = await fetch(`${API_BASE_URL}/activities/${activityId}/attachments`, {
    method: 'POST',
    body: form,
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrf,
      Accept: 'application/json',
      // DO NOT SET Content-Type
    },
  })

  if (!res.ok) {
    // Surface the backend's reason (e.g. INVALID_FILE / ATTACHMENT_LIMIT_EXCEEDED)
    // instead of a generic message, mirroring the shared API() error shape.
    const data = await res.json().catch(() => null)
    throw createApiError({
      message: data?.message || 'Fehler beim Hochladen des Anhangs',
      code: data?.code || 'ACTIVITY_ATTACHMENT_UPLOAD_ERROR',
      errors: data?.errors,
      status: res.status,
      data,
      fileName: file.name,
    })
  }

  return await listActivityAttachments(activityId)
}

export async function deleteActivityAttachment(activityId: number, attachmentId: number | string) {
  return await API(
    `/activities/${activityId}/attachments/${attachmentId}`,
    {
      method: 'DELETE',
    },
    'Fehler beim Löschen des Anhangs',
    'ACTIVITY_ATTACHMENT_DELETE_ERROR'
  )
}
